// ============================================
// PAGE - Detalle Ingreso Producto
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MdArrowBack, MdExpandMore, MdExpandLess, MdClose, MdSearch } from 'react-icons/md';
import imagenNoDisponible from '../../assets/images/imagen_no_disponible.png';
import { InfoTooltip } from '@/components/InfoTooltip';
import {
  useBuscarImagenes, 
  useAgregarProductoAlmacen, 
  usePreciosPromedio, 
  useAssignCategoriaAlmacen, 
  useSession,
  useGranelCategorias,
  useGranelTipos,
  useGranelProductosEstandar,
  type GranelCategoria,
  type GranelTipo,
  type GranelProductoEstandar,
} from '@almacenes/core';
import { CategoriaAlmacenSelector } from '@/components/CategoriaAlmacenSelector';

interface ProductoData {
  id: number | null;
  codigo: string;
  nombre: string;
  marca: string;
  esRetail: boolean;
  posibleRetail: boolean;
  imagenUrl: string;
  imagenProductoId?: number;
}

export const DetalleIngresoProductoPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const producto = location.state?.producto as ProductoData;

  const [precioVenta, setPrecioVenta] = useState('');
  const [precioCompra, setPrecioCompra] = useState('');
  const [margenVenta, setMargenVenta] = useState('');
  const [stock, setStock] = useState('0');
  const [unidad, setUnidad] = useState<'U' | 'F'>('U');
  const [fechaVencimiento, setFechaVencimiento] = useState('');
  const [mostrarCalculadora, setMostrarCalculadora] = useState(false);
  const [nombreProducto, setNombreProducto] = useState(producto?.nombre || '');
  const [codigoInterno, setCodigoInterno] = useState('');
  const [mostrarModalImagen, setMostrarModalImagen] = useState(false);
  const [busquedaImagen, setBusquedaImagen] = useState('');
  const [imagenSeleccionada, setImagenSeleccionada] = useState(producto?.imagenUrl || '');
  const [imagenProductoIdSeleccionada, setImagenProductoIdSeleccionada] = useState<number | undefined>(producto?.imagenProductoId);
  const [errorFechaVencimiento, setErrorFechaVencimiento] = useState('');
  const [errorValidacion, setErrorValidacion] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [categoriaSeleccionadaId, setCategoriaSeleccionadaId] = useState<number | null>(null);

  // Hook para buscar imágenes
  const { data: imagenesEncontradas, isLoading: buscandoImagenes } = useBuscarImagenes(busquedaImagen);

  // Hook para agregar producto al almacén
  const agregarProductoMutation = useAgregarProductoAlmacen();
  const asignarCategoria = useAssignCategoriaAlmacen();

  // Sesión para verificar acceso premium (usado por CategoriaAlmacenSelector)
  const { data: session } = useSession();
  const hasPremiumAccess = session?.hasPremiumAccess ?? false;

  // Hook para obtener precios promedio
  const { data: preciosPromedio, isLoading: cargandoPrecios } = usePreciosPromedio(
    producto?.id || null,
    mostrarCalculadora && !!producto?.id
  );

  // Determinar si el producto tiene código de barra
  const tieneCodigoBarra = producto?.codigo && producto.codigo.trim() !== '';

  // Verificar si debe mostrar la calculadora de precios
  const mostrarCalculadoraPrecios = producto?.id && (producto?.esRetail || producto?.posibleRetail);

  // Estados para granel
  const [granelCategoriaSeleccionada, setGranelCategoriaSeleccionada] = useState<GranelCategoria | null>(null);
  const [granelTipoSeleccionado, setGranelTipoSeleccionado] = useState<GranelTipo | null>(null);
  const [granelMarcaSeleccionada, setGranelMarcaSeleccionada] = useState<GranelProductoEstandar | null>(null);
  const [granelCategoriaBusqueda, setGranelCategoriaBusqueda] = useState('');
  const [granelTipoBusqueda, setGranelTipoBusqueda] = useState('');
  const [granelMarcaBusqueda, setGranelMarcaBusqueda] = useState('');
  const [mostrarDropdownCategoria, setMostrarDropdownCategoria] = useState(false);
  const [mostrarDropdownTipo, setMostrarDropdownTipo] = useState(false);
  const [mostrarDropdownMarca, setMostrarDropdownMarca] = useState(false);
  const [tipoCodigoSeleccionado, setTipoCodigoSeleccionado] = useState<'codigo_interno' | 'generar_codigo'>('codigo_interno');

  // Hooks para obtener datos de granel
  const { data: granelCategorias = [] } = useGranelCategorias(!tieneCodigoBarra);
  const { data: granelTipos = [] } = useGranelTipos(
    granelCategoriaSeleccionada?.id ?? null,
    !tieneCodigoBarra && !!granelCategoriaSeleccionada
  );
  // Las marcas salen de los productos estándar del tipo seleccionado (traen su código de barra)
  const { data: granelMarcas = [] } = useGranelProductosEstandar(
    granelTipoSeleccionado?.id ?? null,
    !tieneCodigoBarra && !!granelTipoSeleccionado
  );

  // Filtrar opciones según búsqueda
  const granelCategoriasFiltradas = granelCategorias.filter(cat =>
    cat.nombre.toLowerCase().includes(granelCategoriaBusqueda.toLowerCase())
  );
  const granelTiposFiltrados = granelTipos.filter(tipo =>
    tipo.nombre.toLowerCase().includes(granelTipoBusqueda.toLowerCase())
  );
  const granelMarcasFiltradas = granelMarcas.filter(marca =>
    marca.marcaNombre.toLowerCase().includes(granelMarcaBusqueda.toLowerCase())
  );

  // El código de barra viene listo desde el producto estándar seleccionado
  const codigoGranelGenerado = granelMarcaSeleccionada?.codigo ?? null;

  // Mostrar sección de código solo si NO tiene código de barra
  const mostrarSeccionCodigo = !tieneCodigoBarra;
  
  // Si se genera código de barra, establecer automáticamente venderPor a Granel
  useEffect(() => {
    if (codigoGranelGenerado && tipoCodigoSeleccionado === 'generar_codigo') {
      setUnidad('F');
    }
  }, [codigoGranelGenerado, tipoCodigoSeleccionado]);

  // useEffect para cerrar dropdowns al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.relative')) {
        setMostrarDropdownCategoria(false);
        setMostrarDropdownTipo(false);
        setMostrarDropdownMarca(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Función para convertir formato chileno a número
  const parseChileanNumber = (value: string): number => {
    if (!value) return 0;
    // Remover puntos (separador de miles) y reemplazar coma por punto (decimal)
    return parseFloat(value.replace(/\./g, '').replace(',', '.')) || 0;
  };

  // Stock admite decimales (granel): la coma es decimal y el punto solo es miles si viene con coma
  // ("1.234,5"); "2.831" sigue siendo decimal. Devuelve NaN si no es un número limpio.
  const parseStock = (value: string): number => {
    const v = value.trim();
    const n = /^\d{1,3}(\.\d{3})+,\d*$/.test(v) ? v.replace(/\./g, '').replace(',', '.') : v.replace(',', '.');
    return /^(\d+\.?\d*|\.\d+)$/.test(n) ? parseFloat(n) : NaN;
  };

  // Función para calcular margen de venta (sobre el costo)
  const calcularMargen = (venta: string, compra: string): string => {
    const ventaNum = parseChileanNumber(venta);
    const compraNum = parseChileanNumber(compra);
    
    if (ventaNum === 0 || compraNum === 0) return '';
    
    // Margen sobre el costo: ((venta - compra) / compra) * 100
    const margen = ((ventaNum - compraNum) / compraNum) * 100;
    
    // Formatear a máximo 2 decimales
    return margen.toFixed(2).replace('.', ',');
  };

  // Función para redondear según regla: <=0.5 hacia abajo, >0.5 hacia arriba
  const redondearPrecio = (value: number): number => {
    const decimal = value - Math.floor(value);
    if (decimal <= 0.5) {
      return Math.floor(value);
    } else {
      return Math.ceil(value);
    }
  };

  // Función para formatear número a formato chileno sin decimales
  const formatChileanNumber = (value: number): string => {
    const rounded = redondearPrecio(value);
    return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  // Función para calcular precio de venta desde margen y precio de compra (margen sobre el costo)
  const calcularPrecioVenta = (margen: string, compra: string): string => {
    const margenNum = parseChileanNumber(margen);
    const compraNum = parseChileanNumber(compra);
    
    if (compraNum === 0) return '';
    
    // Fórmula con margen sobre el costo: venta = compra * (1 + margen/100)
    const venta = compraNum * (1 + margenNum / 100);
    
    return formatChileanNumber(venta);
  };

  const handleVolver = () => {
    navigate('/inventario/ingresar');
  };

  // ---------- GRANEL: Handlers ----------
  const handleGranelCategoriaSelect = (categoria: GranelCategoria) => {
    setGranelCategoriaSeleccionada(categoria);
    setGranelCategoriaBusqueda(categoria.nombre);
    setMostrarDropdownCategoria(false);
    // Resetear tipo cuando cambia la categoría
    setGranelTipoSeleccionado(null);
    setGranelTipoBusqueda('');
    setGranelMarcaSeleccionada(null);
    setGranelMarcaBusqueda('');
  };

  const handleGranelTipoSelect = (tipo: GranelTipo) => {
    setGranelTipoSeleccionado(tipo);
    setGranelTipoBusqueda(tipo.nombre);
    setMostrarDropdownTipo(false);
    // Resetear marca cuando cambia el tipo
    setGranelMarcaSeleccionada(null);
    setGranelMarcaBusqueda('');
  };

  const handleGranelMarcaSelect = (marca: GranelProductoEstandar) => {
    setGranelMarcaSeleccionada(marca);
    setGranelMarcaBusqueda(marca.marcaNombre);
    setMostrarDropdownMarca(false);
  };

  const handleGuardar = async () => {
    // Limpiar errores previos
    setErrorValidacion('');

    // Validar fecha antes de guardar
    if (fechaVencimiento) {
      const resultadoValidacion = validarFecha(fechaVencimiento);
      if (!resultadoValidacion.valido) {
        setErrorFechaVencimiento(resultadoValidacion.mensaje);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    // Validar campos obligatorios
    const errores: string[] = [];

    // Nombre del producto (obligatorio si no es retail)
    if (!producto.esRetail && !nombreProducto.trim()) {
      errores.push('Nombre del producto');
    }

    // Validar campos de granel si se está generando código
    if (!tieneCodigoBarra && tipoCodigoSeleccionado === 'generar_codigo') {
      if (!granelCategoriaSeleccionada) {
        errores.push('Categoría de granel');
      }
      if (!granelTipoSeleccionado) {
        errores.push('Tipo de granel');
      }
      if (!granelMarcaSeleccionada) {
        errores.push('Marca de granel');
      }
    }

    // Precio de venta (obligatorio)
    if (!precioVenta || parseChileanNumber(precioVenta) === 0) {
      errores.push('Precio de venta');
    }

    // Precio de compra (obligatorio)
    if (!precioCompra || parseChileanNumber(precioCompra) === 0) {
      errores.push('Precio de compra');
    }

    // Stock (obligatorio)
    if (!(parseStock(stock) > 0)) {
      errores.push('Stock');
    }

    if (errores.length > 0) {
      setErrorValidacion(`Campos obligatorios faltantes: ${errores.join(', ')}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (unidad === 'U' && !Number.isInteger(parseStock(stock))) {
      setErrorValidacion('El stock debe ser un número entero para productos vendidos por unidad');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Convertir fecha de dd/mm/aaaa a formato ISO (aaaa-mm-dd) si existe
    let fechaVencimientoISO: string | null = null;
    if (fechaVencimiento) {
      const [dia, mes, anio] = fechaVencimiento.split('/');
      fechaVencimientoISO = `${anio}-${mes}-${dia}`;
    }

    // Preparar datos para enviar
    const datos: any = {
      productoId: producto?.id,
      productoCodigo: tieneCodigoBarra 
        ? producto.codigo 
        : (tipoCodigoSeleccionado === 'generar_codigo' ? (codigoGranelGenerado || '') : codigoInterno || ''),
      productoNombre: producto.esRetail ? producto.nombre : nombreProducto,
      imagenProductoId: imagenProductoIdSeleccionada,
      precioCompraActual: parseChileanNumber(precioCompra),
      precioVentaActual: parseChileanNumber(precioVenta),
      venderPor: unidad,
      stock: parseStock(stock),
      fechaVencimiento: fechaVencimientoISO,
      esPack: false,
    };

    // Agregar información de granel si se generó un código
    if (tipoCodigoSeleccionado === 'generar_codigo' && codigoGranelGenerado && 
        granelMarcaSeleccionada) {
      datos.granelProductoEstandarId = granelMarcaSeleccionada.id;
    }

    try {
      setGuardando(true);
      const resultado = await agregarProductoMutation.mutateAsync(datos);

      // Asignar categoría si el usuario seleccionó una (no es bloqueante)
      let mensajeExito = `Producto agregado exitosamente: ${resultado.productoNombre}`;
      if (hasPremiumAccess && categoriaSeleccionadaId !== null) {
        try {
          await asignarCategoria.mutateAsync({
            productoAlmacenId: resultado.productoAlmacenId,
            categoriaAlmacenId: categoriaSeleccionadaId,
          });
        } catch {
          // El producto ya fue creado — informamos sin bloquear la navegación
          mensajeExito = `${resultado.productoNombre} agregado, pero no se pudo asignar la categoría. Puedes asignarla editando el producto.`;
        }
      }

      navigate('/inventario/ingresar', {
        state: { mensajeExito },
        replace: true,
      });
    } catch (error: any) {
      // Verificar si es error 409 (producto ya existe)
      const status = error?.statusCode;
      if (status === 409) {
        const mensaje = error.message || 'Este producto ya se encuentra registrado en nuestro inventario.';
        setErrorValidacion(mensaje);
      } else {
        const mensaje = error?.message || 'Error al guardar el producto. Intenta nuevamente.';
        setErrorValidacion(mensaje);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setGuardando(false);
    }
  };

  const handlePrecioVentaChange = (value: string) => {
    // Permitir solo números
    const cleanValue = value.replace(/[^\d]/g, '');
    
    // Formatear con separador de miles
    const formattedValue = cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
    setPrecioVenta(formattedValue);
    
    // Calcular margen automáticamente si hay precio de compra
    if (precioCompra) {
      const nuevoMargen = calcularMargen(formattedValue, precioCompra);
      setMargenVenta(nuevoMargen);
    }
  };

  const handlePrecioCompraChange = (value: string) => {
    // Permitir solo números
    const cleanValue = value.replace(/[^\d]/g, '');
    
    // Formatear con separador de miles
    const formattedValue = cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
    setPrecioCompra(formattedValue);
    
    // Calcular margen automáticamente si hay precio de venta
    if (precioVenta) {
      const nuevoMargen = calcularMargen(precioVenta, formattedValue);
      setMargenVenta(nuevoMargen);
    }
  };

  const handleMargenChange = (value: string) => {
    // Permitir solo números y coma para decimales
    const cleanValue = value.replace(/[^\d,]/g, '');
    
    // Separar parte entera y decimal
    const parts = cleanValue.split(',');
    const integerPart = parts[0];
    const decimalPart = parts[1] || '';
    
    // Limitar a 999% máximo (límite razonable)
    if (parseInt(integerPart) > 999) return;
    
    // Reconstruir el valor
    const formattedValue = decimalPart 
      ? `${integerPart},${decimalPart.slice(0, 2)}` // Limitar a 2 decimales
      : integerPart;
    
    setMargenVenta(formattedValue);
    
    // Calcular precio de venta automáticamente si hay precio de compra
    if (precioCompra && formattedValue) {
      const nuevoPrecioVenta = calcularPrecioVenta(formattedValue, precioCompra);
      if (nuevoPrecioVenta) {
        setPrecioVenta(nuevoPrecioVenta);
      }
    }
  };

  const handleSeleccionarImagen = (url: string, id: number) => {
    setImagenSeleccionada(url);
    setImagenProductoIdSeleccionada(id);
    setMostrarModalImagen(false);
    setBusquedaImagen('');
  };

  const validarFecha = (fecha: string): { valido: boolean; mensaje: string } => {
    if (!fecha) return { valido: true, mensaje: '' }; // Campo opcional
    
    // Validar formato dd/mm/aaaa
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = fecha.match(regex);
    
    if (!match) return { valido: false, mensaje: 'Formato inválido (dd/mm/aaaa)' };
    
    const dia = parseInt(match[1], 10);
    const mes = parseInt(match[2], 10);
    const año = parseInt(match[3], 10);
    
    // Validar rangos básicos
    if (mes < 1 || mes > 12) return { valido: false, mensaje: 'Mes inválido' };
    if (dia < 1 || dia > 31) return { valido: false, mensaje: 'Día inválido' };
    if (año < 2000 || año > 2100) return { valido: false, mensaje: 'Año inválido' };
    
    // Crear fecha y validar que sea válida
    const fechaObj = new Date(año, mes - 1, dia);
    
    // Verificar que la fecha creada corresponde a los valores ingresados
    if (fechaObj.getDate() !== dia || fechaObj.getMonth() !== mes - 1 || fechaObj.getFullYear() !== año) {
      return { valido: false, mensaje: 'Fecha inválida' };
    }
    
    // Validar que no sea una fecha pasada (solo fecha, sin hora)
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    fechaObj.setHours(0, 0, 0, 0);
    
    if (fechaObj < hoy) {
      return { valido: false, mensaje: 'La fecha no puede ser anterior a hoy' };
    }
    
    return { valido: true, mensaje: '' };
  };

  const handleFechaVencimientoChange = (value: string) => {
    // Permitir solo números y /
    const cleanValue = value.replace(/[^\d/]/g, '');
    
    // Limitar longitud máxima (10 caracteres: dd/mm/aaaa)
    if (cleanValue.length > 10) return;
    
    // Remover todos los / para trabajar solo con números
    const numbersOnly = cleanValue.replace(/\//g, '');
    let formattedValue = '';
    
    // Formatear según la cantidad de números
    if (numbersOnly.length <= 2) {
      // Solo día: dd
      formattedValue = numbersOnly;
    } else if (numbersOnly.length <= 4) {
      // Día y mes: dd/mm
      formattedValue = numbersOnly.slice(0, 2) + '/' + numbersOnly.slice(2);
    } else {
      // Día, mes y año: dd/mm/aaaa
      formattedValue = numbersOnly.slice(0, 2) + '/' + numbersOnly.slice(2, 4) + '/' + numbersOnly.slice(4, 8);
    }
    
    setFechaVencimiento(formattedValue);
    
    // Validar solo cuando se completa el formato
    if (formattedValue.length === 10) {
      const resultadoValidacion = validarFecha(formattedValue);
      if (!resultadoValidacion.valido) {
        setErrorFechaVencimiento(resultadoValidacion.mensaje);
      } else {
        setErrorFechaVencimiento('');
      }
    } else {
      setErrorFechaVencimiento('');
    }
  };

  if (!producto) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600 mb-4">No hay producto seleccionado</p>
          <button
            onClick={handleVolver}
            className="px-6 py-3 rounded-lg"
            style={{ backgroundColor: '#FACC15', color: '#11194F' }}
          >
            Volver al buscador
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen">
      <div className="px-4 md:px-6 lg:px-8 py-6">
        {/* Contenedor centrado */}
        <div className="max-w-7xl mx-auto">
          {/* Layout de 2 columnas */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Columna izquierda - Información del producto */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
                {/* Botón volver */}
                <button
                  onClick={handleVolver}
                  className="p-2 rounded-lg transition-all hover:bg-gray-200 mb-6"
                  style={{ color: '#11194F' }}
                >
                  <MdArrowBack className="text-2xl" />
                </button>

                {/* Imagen del producto */}
                <div className="flex justify-center mb-6">
                  <button
                    onClick={() => setMostrarModalImagen(true)}
                    className="relative group cursor-pointer"
                  >
                    <img 
                      src={imagenSeleccionada || imagenNoDisponible} 
                      alt={producto.nombre}
                      className="h-48 w-auto object-contain rounded-lg transition-opacity group-hover:opacity-75"
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="bg-black bg-opacity-60 text-white px-4 py-2 rounded-lg text-sm font-semibold">
                        Cambiar imagen
                      </div>
                    </div>
                  </button>
                </div>

                {/* Nombre (solo si es retail) */}
                {producto.esRetail && (
                  <div className="text-center mb-6">
                    <h2 className="text-xl font-bold mb-1" style={{ color: '#11194F' }}>
                      {producto.nombre}
                    </h2>
                  </div>
                )}

                {/* SKU o mensaje sin código */}
                <div className="text-center mb-6">
                  {tieneCodigoBarra ? (
                    <p className="text-gray-500 text-sm">
                      sku: {producto.codigo}
                    </p>
                  ) : (
                    <p className="text-gray-500 text-sm italic">
                      ¿Sin código de barra? Agrega la imagen y listo.
                    </p>
                  )}
                </div>

                {/* Calculadora de precios (condicional) */}
                {mostrarCalculadoraPrecios && (
                  <div>
                    <button
                      onClick={() => setMostrarCalculadora(!mostrarCalculadora)}
                      className="w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all hover:bg-gray-50"
                      style={{ borderColor: '#11194F' }}
                    >
                      <span className="font-semibold text-base" style={{ color: '#11194F' }}>
                        Calcular precio
                      </span>
                      {mostrarCalculadora ? (
                        <MdExpandLess className="text-2xl" style={{ color: '#11194F' }} />
                      ) : (
                        <MdExpandMore className="text-2xl" style={{ color: '#11194F' }} />
                      )}
                    </button>

                    {mostrarCalculadora && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
                        {cargandoPrecios ? (
                          <div className="flex justify-center py-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                          </div>
                        ) : preciosPromedio ? (
                          <>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-600">Precio Promedio Compra</span>
                              <span className="text-xl font-bold text-orange-600">
                                ${preciosPromedio.precioCompraPromedio.toLocaleString('es-CL')}
                              </span>
                            </div>
                            <div className="h-px bg-gray-300"></div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-600">Precio Promedio Venta</span>
                              <span className="text-xl font-bold text-green-600">
                                ${preciosPromedio.precioVentaPromedio.toLocaleString('es-CL')}
                              </span>
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-2">
                            <p className="text-sm text-gray-500">No hay datos de precios disponibles</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Columna derecha - Formulario */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg p-8 md:p-9">                

              {/* Formulario */}
              <div className="space-y-6">
              {/* Sección de código (solo si NO tiene código de barra) */}
              {mostrarSeccionCodigo && (
                <div className="border-2 border-gray-200 rounded-2xl p-6 space-y-4">
                  <h3 className="text-lg font-bold mb-4" style={{ color: '#11194F' }}>
                    Código del Producto
                  </h3>
                  
                  {/* Selector de tipo de código */}
                  <div className="flex gap-4">
                    <button
                      onClick={() => setTipoCodigoSeleccionado('codigo_interno')}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 font-semibold transition-all ${
                        tipoCodigoSeleccionado === 'codigo_interno'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      Código Interno
                    </button>
                    <button
                      onClick={() => setTipoCodigoSeleccionado('generar_codigo')}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 font-semibold transition-all ${
                        tipoCodigoSeleccionado === 'generar_codigo'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      Generar Código (Granel)
                    </button>
                  </div>

                  {/* Formulario de código interno */}
                  {tipoCodigoSeleccionado === 'codigo_interno' && (
                    <div>
                      <label className="block text-base font-semibold mb-2" style={{ color: '#11194F' }}>
                        Código interno <span className="text-gray-400 font-normal">(opcional)</span>
                      </label>
                      <input
                        type="text"
                        value={codigoInterno}
                        onChange={(e) => setCodigoInterno(e.target.value)}
                        placeholder="Ingresa un código interno..."
                        className="w-full px-4 py-3 text-lg border-2 rounded-lg focus:outline-none focus:border-primary transition-colors"
                        style={{ borderColor: '#E5E7EB' }}
                      />
                      <p className="mt-2 text-sm text-gray-500">
                        Este código es opcional y solo para uso interno de tu negocio.
                      </p>
                    </div>
                  )}

                  {/* Formulario de generación de código de barra */}
                  {tipoCodigoSeleccionado === 'generar_codigo' && (
                <div className="space-y-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                    <p className="text-xs text-blue-900">
                      <span className="font-semibold">💡</span> Al generar un código, el producto se venderá por <span className="font-bold">Granel (kg/lt)</span>.
                    </p>
                  </div>

                  {/* Línea con los 3 campos */}
                  <div className="flex flex-wrap lg:flex-nowrap items-start gap-2">
                    {/* Categoría */}
                    <div className="relative flex-1 min-w-[140px]">
                      <label className="block text-xs font-semibold mb-1" style={{ color: '#11194F' }}>
                        1. Categoría <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        value={granelCategoriaBusqueda}
                        onChange={(e) => {
                          setGranelCategoriaBusqueda(e.target.value);
                          setMostrarDropdownCategoria(true);
                        }}
                        onFocus={() => setMostrarDropdownCategoria(true)}
                        placeholder="Buscar..."
                        className="w-full px-2 py-1.5 text-sm border-2 rounded-lg focus:outline-none focus:border-blue-500 transition-colors bg-white"
                        style={{ borderColor: '#E5E7EB' }}
                      />
                      {mostrarDropdownCategoria && (
                        <div className="absolute z-10 w-full mt-1 bg-white border-2 border-blue-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {granelCategoriasFiltradas.length > 0 ? (
                            granelCategoriasFiltradas.map((cat) => (
                              <button
                                key={cat.id}
                                onClick={() => handleGranelCategoriaSelect(cat)}
                                className="w-full px-3 py-2 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0"
                              >
                                <div className="font-medium text-sm text-gray-900">{cat.nombre}</div>
                                {cat.descripcion && <div className="text-xs text-gray-500">{cat.descripcion}</div>}
                              </button>
                            ))
                          ) : (
                            <div className="p-3 text-sm text-gray-500">Sin resultados</div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="hidden lg:flex items-center pt-5 text-gray-400">→</div>

                    {/* Tipo */}
                    <div className="relative flex-1 min-w-[140px]">
                      <label className="block text-xs font-semibold mb-1" style={{ color: '#11194F' }}>
                        2. Tipo <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        value={granelTipoBusqueda}
                        onChange={(e) => {
                          setGranelTipoBusqueda(e.target.value);
                          setMostrarDropdownTipo(true);
                        }}
                        onFocus={() => setMostrarDropdownTipo(true)}
                        placeholder="Buscar..."
                        disabled={!granelCategoriaSeleccionada}
                        className="w-full px-2 py-1.5 text-sm border-2 rounded-lg focus:outline-none focus:border-blue-500 transition-colors bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                        style={{ borderColor: '#E5E7EB' }}
                      />
                      {mostrarDropdownTipo && (
                        <div className="absolute z-10 w-full mt-1 bg-white border-2 border-blue-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {granelTiposFiltrados.length > 0 ? (
                            granelTiposFiltrados.map((tipo) => (
                              <button
                                key={tipo.id}
                                onClick={() => handleGranelTipoSelect(tipo)}
                                className="w-full px-3 py-2 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0"
                              >
                                <div className="font-medium text-sm text-gray-900">{tipo.nombre}</div>
                              </button>
                            ))
                          ) : (
                            <div className="p-3 text-sm text-gray-500">Sin resultados</div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="hidden lg:flex items-center pt-5 text-gray-400">→</div>

                    {/* Marca */}
                    <div className="relative flex-1 min-w-[140px]">
                      <label className="block text-xs font-semibold mb-1" style={{ color: '#11194F' }}>
                        3. Marca <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        value={granelMarcaBusqueda}
                        onChange={(e) => {
                          setGranelMarcaBusqueda(e.target.value);
                          setMostrarDropdownMarca(true);
                        }}
                        onFocus={() => setMostrarDropdownMarca(true)}
                        placeholder="Buscar..."
                        disabled={!granelTipoSeleccionado}
                        className="w-full px-2 py-1.5 text-sm border-2 rounded-lg focus:outline-none focus:border-blue-500 transition-colors bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                        style={{ borderColor: '#E5E7EB' }}
                      />
                      {mostrarDropdownMarca && (
                        <div className="absolute z-10 w-full mt-1 bg-white border-2 border-blue-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {granelMarcasFiltradas.length > 0 ? (
                            granelMarcasFiltradas.map((marca) => (
                              <button
                                key={marca.id}
                                onClick={() => handleGranelMarcaSelect(marca)}
                                className="w-full px-3 py-2 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0"
                              >
                                <div className="font-medium text-sm text-gray-900">{marca.marcaNombre}</div>
                              </button>
                            ))
                          ) : (
                            <div className="p-3 text-sm text-gray-500">Sin resultados</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Código EAN-13 generado (línea separada abajo) */}
                  {codigoGranelGenerado ? (
                    <div className="mt-3">
                      <div className="flex items-center justify-center gap-2 text-gray-500 mb-2">
                        <div className="flex-1 h-px bg-gray-300"></div>
                        <span className="text-xs font-semibold">Resultado</span>
                        <div className="flex-1 h-px bg-gray-300"></div>
                      </div>
                      <div className="flex items-center justify-center gap-3 px-4 py-3 bg-blue-50 border-2 border-blue-300 rounded-xl">
                        <div className="text-center">
                          <p className="text-xs font-semibold text-gray-600 mb-1">
                            Código EAN-13 generado
                          </p>
                          <p className="text-2xl font-bold text-blue-600 font-mono tracking-wider">
                            {codigoGranelGenerado}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Dígito verificador: <span className="font-semibold">{codigoGranelGenerado.slice(-1)}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl text-center">
                      <p className="text-sm text-gray-400 italic">
                        Selecciona categoría, tipo y marca para generar el código EAN-13
                      </p>
                    </div>
                  )}
                </div>
                  )}
                </div>
              )}

              {/* Código interno simple (si NO tiene feature flag habilitado) */}
              {mostrarSeccionCodigo && (
                <div>
                  <label className="block text-base font-semibold mb-2" style={{ color: '#11194F' }}>
                    Código interno <span className="text-gray-400 font-normal">(opcional)</span>
                  </label>
                  <input
                    type="text"
                    value={codigoInterno}
                    onChange={(e) => setCodigoInterno(e.target.value)}
                    placeholder="Ingresa un código interno..."
                    className="w-full px-4 py-3 text-lg border-2 rounded-lg focus:outline-none focus:border-primary transition-colors"
                    style={{ borderColor: '#E5E7EB' }}
                  />
                </div>
              )}

              {/* Nombre del producto (solo si NO es retail) */}
              {!producto.esRetail && (
                <div>
                  <label className="block text-base font-semibold mb-2" style={{ color: '#11194F' }}>
                    Nombre del producto <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={nombreProducto}
                    onChange={(e) => setNombreProducto(e.target.value)}
                    className="w-full px-4 py-3 text-lg border-2 rounded-lg focus:outline-none focus:border-primary transition-colors"
                    style={{ borderColor: '#E5E7EB' }}
                  />
                </div>
              )}

              {/* Precio de venta y Precio de compra en una fila */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-base font-semibold mb-2" style={{ color: '#11194F' }}>
                    Precio de venta <span className="text-red-600">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="text"
                      value={precioVenta}
                      onChange={(e) => handlePrecioVentaChange(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 text-lg border-2 rounded-lg focus:outline-none focus:border-primary transition-colors"
                      style={{ borderColor: '#E5E7EB' }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-base font-semibold mb-2" style={{ color: '#11194F' }}>
                    Precio de compra <span className="text-red-600">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="text"
                      value={precioCompra}
                      onChange={(e) => handlePrecioCompraChange(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 text-lg border-2 rounded-lg focus:outline-none focus:border-primary transition-colors"
                      style={{ borderColor: '#E5E7EB' }}
                    />
                  </div>
                </div>
              </div>

              {/* Margen de venta y Stock en una fila */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-1.5 text-base font-semibold mb-2" style={{ color: '#11194F' }}>
                    Margen de venta
                    <InfoTooltip text="Este porcentaje se calcula sobre el precio de compra. Si compras a $1.000 y quieres ganar 30%, tu precio de venta será $1.300." />
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">%</span>
                    <input
                      type="text"
                      value={margenVenta}
                      onChange={(e) => handleMargenChange(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 text-lg border-2 rounded-lg focus:outline-none focus:border-primary transition-colors"
                      style={{ borderColor: '#E5E7EB' }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-base font-semibold mb-2" style={{ color: '#11194F' }}>
                    Stock <span className="text-red-600">*</span>
                  </label>
                  <div className="flex">
                    <input
                      type="text"
                      value={stock}
                      onChange={(e) => setStock(e.target.value)}
                      className="w-20 md:w-24 px-3 py-3 text-lg border-2 border-r-0 rounded-l-lg focus:outline-none focus:border-primary transition-colors"
                      style={{ borderColor: '#E5E7EB' }}
                    />
                    <select
                      value={unidad}
                      onChange={(e) => setUnidad(e.target.value as 'U' | 'F')}
                      disabled={tipoCodigoSeleccionado === 'generar_codigo' && !!codigoGranelGenerado}
                      className="px-4 py-3 text-lg border-2 rounded-r-lg focus:outline-none focus:border-primary transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                      style={{ borderColor: '#E5E7EB', color: '#11194F' }}
                    >
                      <option value="U">Unidad</option>
                      <option value="F">Granel</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Fecha de vencimiento */}
                <div>
                  <label className="block text-base font-semibold mb-2" style={{ color: '#11194F' }}>
                    Fecha Vencimiento <span className="text-gray-400 font-normal">(opcional)</span>
                  </label>
                  <input
                    type="text"
                    value={fechaVencimiento}
                    onChange={(e) => handleFechaVencimientoChange(e.target.value)}
                    placeholder="dd/mm/aaaa"
                    className={`w-full px-4 py-3 text-lg border-2 rounded-lg focus:outline-none transition-colors ${
                      errorFechaVencimiento ? 'border-red-500' : 'focus:border-primary'
                    }`}
                    style={{ borderColor: errorFechaVencimiento ? '#EF4444' : '#E5E7EB' }}
                  />
                  {errorFechaVencimiento && (
                    <p className="mt-1 text-sm text-red-600">
                      {errorFechaVencimiento}
                    </p>
                  )}
                </div>

                {/* Categoría del negocio (solo premium) */}
                <div>
                  <label className="block text-base font-semibold mb-2" style={{ color: '#11194F' }}>
                    Categoría del negocio{' '}
                    <span className="text-gray-400 font-normal">(opcional)</span>
                  </label>
                  <CategoriaAlmacenSelector
                    value={categoriaSeleccionadaId}
                    onChange={setCategoriaSeleccionadaId}
                    disabled={guardando}
                  />
                </div>
              </div>

              {/* Mensaje de error de validación */}
                {errorValidacion && (
                  <div className="mb-6 p-4 rounded-xl bg-red-50 border-2 border-red-200">
                    <div className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <p className="font-semibold text-lg text-red-700">
                          {errorValidacion}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              {/* Botón Guardar */}
              <button
                onClick={handleGuardar}
                disabled={guardando}
                className="w-full py-4 rounded-xl text-xl font-semibold transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                style={{ backgroundColor: '#43BFFF', color: '#FFFFFF' }}
              >
                {guardando && (
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                )}
                {guardando ? 'Guardando...' : 'Guardar producto'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

      {/* Modal de búsqueda de imágenes */}
      {mostrarModalImagen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-start justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl my-8 max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header del modal */}
            <div className="flex items-center justify-between p-6 border-b-2 border-gray-200">
              <h2 className="text-2xl font-bold" style={{ color: '#11194F' }}>
                Buscar imagen del producto
              </h2>
              <button
                onClick={() => {
                  setMostrarModalImagen(false);
                  setBusquedaImagen('');
                }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                style={{ color: '#11194F' }}
              >
                <MdClose className="text-3xl" />
              </button>
            </div>

            {/* Buscador */}
            <div className="p-6 border-b-2 border-gray-200">
              <div className="relative">
                <MdSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-gray-400" />
                <input
                  type="text"
                  value={busquedaImagen}
                  onChange={(e) => setBusquedaImagen(e.target.value)}
                  placeholder="Escribe al menos 3 caracteres para buscar..."
                  className="w-full pl-12 pr-4 py-4 text-lg border-2 rounded-xl focus:outline-none focus:border-primary transition-colors"
                  style={{ borderColor: '#E5E7EB' }}
                  autoFocus
                />
              </div>
              {busquedaImagen.length > 0 && busquedaImagen.length < 3 && (
                <p className="mt-2 text-sm text-gray-500">
                  Escribe al menos {3 - busquedaImagen.length} caracteres más para buscar
                </p>
              )}
            </div>

            {/* Resultados */}
            <div className="flex-1 overflow-y-auto p-6">
              {buscandoImagenes && (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
                  <p className="text-gray-600">Buscando imágenes...</p>
                </div>
              )}

              {!buscandoImagenes && busquedaImagen.length >= 3 && (!imagenesEncontradas || imagenesEncontradas.length === 0) && (
                <div className="flex flex-col items-center justify-center py-12">
                  <p className="text-xl text-gray-600">No se encontraron imágenes</p>
                  <p className="text-gray-500 mt-2">Intenta con otra búsqueda</p>
                </div>
              )}

              {!buscandoImagenes && imagenesEncontradas && imagenesEncontradas.length > 0 && (
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Se encontraron {imagenesEncontradas.length} imágenes
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {imagenesEncontradas.map((imagen) => (
                      <button
                        key={imagen.id}
                        onClick={() => handleSeleccionarImagen(imagen.url, imagen.id)}
                        className="group relative border-2 border-gray-200 rounded-xl p-3 hover:border-primary hover:shadow-lg transition-all"
                      >
                        <div className="aspect-square flex items-center justify-center bg-gray-50 rounded-lg overflow-hidden mb-2">
                          <img
                            src={imagen.url}
                            alt={imagen.descripcion}
                            className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform"
                          />
                        </div>
                        <p className="text-xs text-gray-700 line-clamp-2 mb-1">
                          {imagen.descripcion}
                        </p>
                        <p className="text-xs text-gray-500">
                          {imagen.fuente}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {busquedaImagen.length < 3 && !buscandoImagenes && (
                <div className="flex flex-col items-center justify-center py-12">
                  <MdSearch className="text-6xl text-gray-300 mb-4" />
                  <p className="text-xl text-gray-600">Busca una imagen</p>
                  <p className="text-gray-500 mt-2">Escribe el nombre del producto para ver resultados</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </>
  );
};
